// src/dashboard/pages/teacher/Categories/components/CategoriesMembersTable.jsx

import React from "react";
import { FaEdit } from "react-icons/fa";

import {
  formatDateFr,
  getMemberPhoto,
} from "../utils/categoriesDashboardUtils";

function WeightCell({ member, onUpdateWeight, updatingWeightId }) {
  const hasWeight = Number.isFinite(Number(member.poids)) && Number(member.poids) > 0;

  const handleClick = () => {
    const currentValue = hasWeight ? String(member.poids) : "";

    const value = window.prompt(
      `Nouveau poids pour ${(member.nom || "").toUpperCase()} ${
        member.prenom || ""
      } en kg :`,
      currentValue
    );

    if (value === null) return;

    onUpdateWeight?.(member, value);
  };

  return (
    <div className="flex items-center gap-2">
      {hasWeight ? (
        <span className="inline-flex min-w-16 justify-center rounded-2xl border border-slate-200 bg-white px-3 py-1 text-sm font-bold text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white">
          {Number(member.poids)} kg
        </span>
      ) : (
        <span className="inline-flex rounded-full bg-rose-50 px-2.5 py-1 text-xs font-bold text-rose-700 ring-1 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/20">
          À peser
        </span>
      )}

      <button
        type="button"
        onClick={handleClick}
        disabled={updatingWeightId === member.id}
        className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 hover:text-indigo-700 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
        title={hasWeight ? "Modifier le poids" : "Renseigner le poids"}
      >
        <FaEdit />
      </button>
    </div>
  );
}

export default function CategoriesMembersTable({
  members = [],
  onUpdateWeight,
  updatingWeightId,
}) {
  return (
    <div className="hidden md:block">
      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900/60">
        <table className="min-w-[1260px] w-full">
          <thead className="sticky top-0 z-20">
            <tr className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600 dark:bg-slate-900 dark:text-slate-300">
              <th className="px-4 py-3 text-left font-bold">Adhérent</th>
              <th className="px-4 py-3 text-left font-bold">Genre</th>
              <th className="px-4 py-3 text-left font-bold">Naissance</th>
              <th className="px-4 py-3 text-left font-bold">Âge saison</th>
              <th className="px-4 py-3 text-left font-bold">Catégorie âge</th>
              <th className="px-4 py-3 text-left font-bold">Poids</th>
              <th className="px-4 py-3 text-left font-bold">
                Catégorie poids
              </th>
              <th className="px-4 py-3 text-left font-bold">Groupe(s)</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
            {members.map((member) => (
              <tr
                key={member.id}
                className="transition hover:bg-slate-50/80 dark:hover:bg-slate-800/40"
              >
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <img
                      src={getMemberPhoto(member)}
                      alt={`${member.prenom || ""} ${member.nom || ""}`}
                      onError={(event) => {
                        event.currentTarget.src = "/img/user_avatar.jpg";
                      }}
                      className="h-11 w-11 rounded-2xl object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                    />

                    <div>
                      <p className="font-bold text-slate-900 dark:text-white">
                        {(member.nom || "").toUpperCase()}{" "}
                        {member.prenom || ""}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        #{member.id}
                      </p>
                    </div>
                  </div>
                </td>

                <td className="px-4 py-3 font-medium text-slate-700 dark:text-slate-200">
                  {member.genre || "—"}
                </td>

                <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                  {formatDateFr(member.date_naissance)}
                </td>

                <td className="px-4 py-3 font-bold text-slate-900 dark:text-white">
                  {member.age !== null && member.age !== undefined
                    ? `${member.age} ans`
                    : "—"}
                </td>

                <td className="px-4 py-3">
                  {member.ageLabel ? (
                    <span className="inline-flex rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-bold text-indigo-700 ring-1 ring-indigo-200 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/20">
                      {member.ageLabel}
                    </span>
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </td>

                <td className="px-4 py-3">
                  <WeightCell
                    member={member}
                    onUpdateWeight={onUpdateWeight}
                    updatingWeightId={updatingWeightId}
                  />
                </td>

                <td className="px-4 py-3">
                  {member.weightBracket ? (
                    <span className="inline-flex rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20">
                      {member.weightBracket}
                    </span>
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </td>

                <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                  {member.groupsLabel || "—"}
                </td>
              </tr>
            ))}

            {members.length === 0 ? (
              <tr>
                <td
                  colSpan={8}
                  className="px-4 py-10 text-center text-sm text-slate-500 dark:text-slate-400"
                >
                  Aucun adhérent ne correspond aux filtres sélectionnés.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}